/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import { LineChart } from 'lucide-react';
import { useState } from 'react';
import { Navigate, useParams } from 'react-router';

/* Import i18n functions */
import { useI18n } from '@/context/I18nContext';

/* Import configuration */
import { PLOTLY_MIN_WIDTH } from '@/config/app';
import { APP_ROUTES } from '@/config/appRoutes';
import { MUENSTER_DEVICE_SENSOR_CONFIG, NEUTRAUBLING_DEVICE_SENSOR_CONFIG } from '@/config/deviceSensorConfig';
import { createHeatingEnergyDetailKpiConfigs } from '@/config/kpi/detailHeatingEnergy';

/* Import components */
import Api2StoredPlotlyPlot from '@/components/api/Api2StoredPlotlyPlot';
import Api2TimeSeriesPlot from '@/components/api/Api2TimeSeriesPlot';
import KpiCarouselApi2TimeSeries from '@/components/api/KpiCarouselApi2TimeSeries';
import KpiValueApi2TimeSeries from '@/components/api/KpiValueApi2TimeSeries';
import DashboardGrid from '@/components/layout/DashboardGrid';

/* Import libraries */
import { DeviceSensor } from '@/lib/sensors/catalog/deviceSensor';
import { DeviceSensorCatalog } from '@/lib/sensors/catalog/deviceSensorCatalog';
import { getSensorHistoryAxisRange } from '@/lib/timeSeries/sensorHistoryAxisRange';

function HeatingEnergyDetailTab() {
    const { t } = useI18n();
    const { heatingEnergyKey } = useParams<{ heatingEnergyKey: string }>();
    const [selectedChart, setSelectedChart] = useState<
        'sensor-history-3months' | 'sensor-history-12months' | 'prediction'
    >('sensor-history-3months');

    const heatingEnergyDeviceSensorCatalog = new DeviceSensorCatalog(heatingEnergyKey?.startsWith('Mue') ? MUENSTER_DEVICE_SENSOR_CONFIG : NEUTRAUBLING_DEVICE_SENSOR_CONFIG);
    const heatingEnergySystemKeys = heatingEnergyDeviceSensorCatalog.getVirtualDevices('HE');
    const selectedHeatingEnergyKey = heatingEnergyKey && heatingEnergySystemKeys.includes(heatingEnergyKey) ? heatingEnergyKey : null;
    const selectedHeatingEnergyDeviceSensor = selectedHeatingEnergyKey ? new DeviceSensor(selectedHeatingEnergyKey, heatingEnergyDeviceSensorCatalog) : null;

    if (!selectedHeatingEnergyKey || !selectedHeatingEnergyDeviceSensor) {
        return <Navigate to={APP_ROUTES.heatingenergy} replace />;
    }

    const cutoffTemperatureSensorName = selectedHeatingEnergyDeviceSensor.getSensorName('HE', 'CutoffTemperature');
    const weekendReductionPercentSensorName = selectedHeatingEnergyDeviceSensor.getSensorName('HE', 'WeekendReductionPercent');
    const savedEnergySensorName = selectedHeatingEnergyDeviceSensor.getSensorName('HE', 'SavedEnergy');
    const heatingEnergySystemName = selectedHeatingEnergyDeviceSensor.getDeviceName() ?? selectedHeatingEnergyKey;
    const sensorHistoryMonths = selectedChart === 'sensor-history-3months' ? 3 : 12;

    if (
        !cutoffTemperatureSensorName ||
        !weekendReductionPercentSensorName ||
        !savedEnergySensorName
    ) {
        return <Navigate to={APP_ROUTES.heatingenergy} replace />;
    }

    const kpiConfigs = createHeatingEnergyDetailKpiConfigs({
        deviceSensor: selectedHeatingEnergyDeviceSensor,
        t: t,
    });

    if (kpiConfigs === null) {
        return <Navigate to={APP_ROUTES.heatingenergy} replace />;
    }

    return (
        <div className="flex h-full min-h-0 flex-col gap-4">
            <div className="flex shrink-0 flex-wrap items-center justify-between gap-4 text-sm font-medium text-base-content/75">
                <span>
                    <strong>{heatingEnergySystemName}:</strong> {selectedHeatingEnergyKey}
                </span>
                <div className="chart-selection" role="group" aria-label={t('Chart selection')}>
                    <button
                        type="button"
                        className={`chart-selection__button ${selectedChart === 'sensor-history-3months' ? 'chart-selection__button--active' : ''}`}
                        aria-pressed={selectedChart === 'sensor-history-3months'}
                        title={t('Show heating power history for 3 months')}
                        onClick={() => setSelectedChart('sensor-history-3months')}
                    >
                        <LineChart className="h-4 w-4" aria-hidden="true" />
                        {t('Heating power history (3 months)')}
                    </button>
                    <button
                        type="button"
                        className={`chart-selection__button ${selectedChart === 'sensor-history-12months' ? 'chart-selection__button--active' : ''}`}
                        aria-pressed={selectedChart === 'sensor-history-12months'}
                        title={t('Show heating power history for 12 months')}
                        onClick={() => setSelectedChart('sensor-history-12months')}
                    >
                        <LineChart className="h-4 w-4" aria-hidden="true" />
                        {t('Heating power history (12 months)')}
                    </button>
                    <button
                        type="button"
                        className={`chart-selection__button ${selectedChart === 'prediction' ? 'chart-selection__button--active' : ''}`}
                        aria-pressed={selectedChart === 'prediction'}
                        title={t('Show heating energy prediction')}
                        onClick={() => setSelectedChart('prediction')}
                    >
                        <LineChart className="h-4 w-4" aria-hidden="true" />
                        {t('Heating energy prediction')}
                    </button>
                </div>
            </div>
            <DashboardGrid className="min-h-0 flex-1" desktopCols={3} desktopRows={["150px", "1fr"]} breakpoint={"2xl"} aria-label={t('Heating energy dashboard')}>
                <DashboardGrid.Item as="section" className="dashboard-panel">
                    <KpiValueApi2TimeSeries
                        {...kpiConfigs.cutoffTemperature}
                    />
                </DashboardGrid.Item>

                <DashboardGrid.Item as="section" className="dashboard-panel">
                    <KpiValueApi2TimeSeries
                        {...kpiConfigs.weekendReductionPercent}
                    />
                </DashboardGrid.Item>

                <DashboardGrid.Item as="section" className="dashboard-panel">
                    <KpiCarouselApi2TimeSeries
                        {...kpiConfigs.savedEnergy}
                    />
                </DashboardGrid.Item>

                {/* Scrollable panels require a matching dashboard path in src/config/appPageLayout.ts. */}
                <DashboardGrid.Item as="section" className="dashboard-panel" desktopColSpan={3} mobileMinHeight={'750px'} scrollable>
                    {selectedChart === 'prediction' ? (
                        <Api2StoredPlotlyPlot
                            urn={selectedHeatingEnergyKey}
                            reference={'heating_energy'}
                            range="all"
                            title={t('Heating energy prediction {key}', { key: selectedHeatingEnergyKey })}
                        />
                    ) : (
                        <Api2TimeSeriesPlot
                            from={sensorHistoryMonths === 3 ? 'NOW-3MONTHS' : 'NOW-12MONTHS'}
                            to="NOW"
                            urn={selectedHeatingEnergyKey}
                            includeZeroYAxis
                            legendNames={{
                                [selectedHeatingEnergyKey]: t('Heating power history {key}', { key: selectedHeatingEnergyKey }),
                            }}
                            minWidth={PLOTLY_MIN_WIDTH}
                            title={t('Heating power history {key}', { key: selectedHeatingEnergyKey })}
                            xAxisTitle={t('Period')}
                            xAxisRange={getSensorHistoryAxisRange(sensorHistoryMonths)}
                            yAxisTitle={t('Heating power in kW')}
                        />
                    )}
                </DashboardGrid.Item>
            </DashboardGrid>
        </div>
    );
}

export default HeatingEnergyDetailTab;
